/**
 * BuchhaltungAnlagen — Anlagevermögen + AfA-Lauf.
 * Kıymet kaydı (SKR04 sınıf 0 hesapları) ve yıllık lineer AfA buchung'u.
 * AfA-Lauf yıl başına bir kez (DB UNIQUE); ilk yıl ay bazlı pro-rata.
 * Neue Anschaffungen mit Beleg laufen über Ausgaben (Kategorie „Anlagegut“):
 * dort werden Zugang + Vorsteuer gebucht und das Anlagegut hier angelegt.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Factory, Plus, Play, Loader2, Receipt } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/api/AuthContext';
import {
  listAccounts, listFixedAssets, createFixedAsset, runDepreciation,
  yearlyDepreciation, fmtEUR,
} from '@/lib/buchhaltungApi';
import { depreciationAccountFor } from '@/lib/assetAccounts';


const thisYear = new Date().getFullYear();

export default function BuchhaltungAnlagen() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const [runYear, setRunYear] = useState(String(thisYear));
  const [form, setForm] = useState({
    asset_no: '', name: '', asset_account_no: '', acquisition_cost: '',
    useful_life_years: '', activation_date: new Date().toISOString().slice(0, 10),
  });

  const { data: accounts = [] } = useQuery({ queryKey: ['fibu-accounts'], queryFn: () => listAccounts() });
  const { data: assets = [], isLoading } = useQuery({ queryKey: ['fibu-assets'], queryFn: listFixedAssets });

  const assetAccounts = accounts.filter((a) => a.is_asset_account);

  const previewTotal = useMemo(
    () => assets.reduce((s, a) => s + yearlyDepreciation(a, Number(runYear)), 0),
    [assets, runYear]
  );

  const createMutation = useMutation({
    mutationFn: () => createFixedAsset({
      asset_no: form.asset_no,
      name: form.name,
      asset_account_no: form.asset_account_no,
      acquisition_date: form.activation_date,
      activation_date: form.activation_date,
      acquisition_cost: parseFloat(form.acquisition_cost),
      useful_life_years: parseFloat(form.useful_life_years),
      // Fahrzeuge → 6222, immaterielle VG → 6200, sonst 6220
      depreciation_account_no: depreciationAccountFor(form.asset_account_no),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['fibu-assets']);
      toast.success('Anlagegut angelegt.');
      setShowForm(false);
      setForm({ asset_no: '', name: '', asset_account_no: '', acquisition_cost: '', useful_life_years: '', activation_date: new Date().toISOString().slice(0, 10) });
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const runMutation = useMutation({
    mutationFn: () => runDepreciation(Number(runYear), user?.email),
    onSuccess: ({ count, total }) => {
      queryClient.invalidateQueries();
      toast.success(`AfA-Lauf ${runYear}: ${count} Anlagen, ${fmtEUR(total)} gebucht.`);
    },
    onError: (err) => toast.error(err.message),
  });

  const formValid = form.asset_no && form.name && form.asset_account_no
    && parseFloat(form.acquisition_cost) > 0 && parseFloat(form.useful_life_years) > 0;

  return (
    <PageShell
      head={(
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <PageHeader icon={<Factory className="w-5 h-5" />} title="Anlagen & AfA" />
            <Badge variant="outline" className="text-[10px]">{assets.length} Anlagegüter</Badge>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="h-11 gap-1.5 text-xs font-bold">
              <Link to="/buchhaltung/ausgaben"><Receipt className="w-4 h-4" /> Mit Beleg erfassen</Link>
            </Button>
            <Button onClick={() => setShowForm(true)} className="h-11 bg-slate-900 hover:bg-slate-800 gap-1.5 text-xs font-bold">
              <Plus className="w-4 h-4" /> Anlagegut
            </Button>
          </div>
        </div>
      )}
    >

      {/* AfA-Lauf */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">AfA-Lauf (linear, jährlich)</CardTitle></CardHeader>
        <CardContent className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <Select value={runYear} onValueChange={setRunYear}>
            <SelectTrigger className="h-11 sm:w-32 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[thisYear, thisYear - 1].map((y) => (
                <SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex-1 text-xs text-slate-500 px-1">
            Vorschau: <b className="text-slate-900">{fmtEUR(previewTotal)}</b> Abschreibung
            {previewTotal > 0 ? ' — Buchung: AfA-Konto an Anlagenkonto (31.12.)' : ' — nichts zu buchen'}
          </div>
          <Button
            className="h-11 gap-2 bg-slate-900 hover:bg-slate-800 text-xs font-bold"
            disabled={previewTotal <= 0 || runMutation.isPending}
            onClick={() => {
              if (window.confirm(`AfA-Lauf ${runYear} über ${fmtEUR(previewTotal)} buchen? Pro Jahr nur einmal möglich.`)) {
                runMutation.mutate();
              }
            }}
          >
            {runMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            AfA buchen
          </Button>
        </CardContent>
      </Card>

      {/* Anlagen listesi */}
      <div className="space-y-2">
        {isLoading && <p className="text-center text-sm text-slate-400 py-6">Lade Anlagen…</p>}
        {!isLoading && assets.length === 0 && (
          <Card className="border-2 border-dashed">
            <CardContent className="p-10 text-center text-slate-400 text-sm">
              Noch keine Anlagegüter. PKW, Maschinen, Büroeinrichtung etc. hier aktivieren.
            </CardContent>
          </Card>
        )}
        {assets.map((a) => {
          const cost = Number(a.acquisition_cost);
          const accum = Number(a.accumulated_depreciation || 0);
          const bookValue = cost - accum;
          const pct = cost > 0 ? Math.min(100, (accum / cost) * 100) : 0;
          return (
            <Card key={a.id}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate">
                      <span className="font-mono">{a.asset_no}</span> · {a.name}
                      {a.status !== 'active' && (
                        <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-px text-[9px] font-bold text-slate-500">Abgang</span>
                      )}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      Konto {a.asset_account_no} · aktiviert {new Date(a.activation_date).toLocaleDateString('de-DE')} · ND {a.useful_life_years} J.
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-black text-slate-900">{fmtEUR(bookValue)}</p>
                    <p className="text-[10px] text-slate-400">Restbuchwert</p>
                  </div>
                </div>
                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-slate-700 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  AK {fmtEUR(cost)} · kumulierte AfA {fmtEUR(accum)} ({pct.toFixed(0)}%)
                  {a.expense_id && (
                    <button type="button" onClick={() => navigate(`/buchhaltung/ausgaben/${a.expense_id}`)}
                      className="ml-2 inline-flex items-center gap-0.5 font-semibold text-blue-700 hover:underline">
                      <Receipt className="w-3 h-3" /> Beleg
                    </button>
                  )}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Yeni Anlagegut */}
      <Sheet open={showForm} onOpenChange={setShowForm}>
        <SheetContent side="bottom" className="h-[88vh] overflow-y-auto">
          <SheetHeader className="mb-4"><SheetTitle>Anlagegut aktivieren</SheetTitle></SheetHeader>
          <div className="space-y-3 max-w-xl mx-auto pb-8">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Anlagen-Nr.</label>
                <Input value={form.asset_no} onChange={(e) => setForm({ ...form, asset_no: e.target.value })} placeholder="A-001" className="h-11" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Aktivierung</label>
                <Input type="date" value={form.activation_date} onChange={(e) => setForm({ ...form, activation_date: e.target.value })} className="h-11" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Bezeichnung</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Mercedes Sprinter" className="h-11" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Anlagenkonto (SKR04)</label>
              <Select value={form.asset_account_no} onValueChange={(v) => {
                const acct = assetAccounts.find((a) => a.account_no === v);
                setForm({
                  ...form, asset_account_no: v,
                  // Nutzungsdauer önerisi konten-metadata'dan (afa_years)
                  useful_life_years: form.useful_life_years || (acct?.afa_years ? String(acct.afa_years) : ''),
                });
              }}>
                <SelectTrigger className="h-11 text-xs"><SelectValue placeholder="Konto wählen…" /></SelectTrigger>
                <SelectContent>
                  {assetAccounts.map((a) => (
                    <SelectItem key={a.account_no} value={a.account_no} className="text-xs">
                      {a.account_no} · {a.name}{a.afa_years ? ` (ND ~${a.afa_years} J.)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">AK/HK (€ netto)</label>
                <Input type="number" inputMode="decimal" value={form.acquisition_cost}
                  onChange={(e) => setForm({ ...form, acquisition_cost: e.target.value })} className="h-11" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Nutzungsdauer (Jahre)</label>
                <Input type="number" inputMode="decimal" value={form.useful_life_years}
                  onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} className="h-11" />
              </div>
            </div>
            <Button className="w-full h-12 bg-slate-900 hover:bg-slate-800 font-bold"
              disabled={!formValid || createMutation.isPending}
              onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aktivieren'}
            </Button>
            <p className="text-[10px] text-slate-400 text-center">
              Nur für bereits gebuchte Anlagen (z. B. Übernahme aus dem Vorsystem) — hier wird keine Anschaffung gebucht.
              Neue Anschaffung mit Beleg: unter <Link to="/buchhaltung/ausgaben" className="underline">Ausgaben</Link> erfassen
              und Kategorie „Anlagegut“ wählen; Zugang und Vorsteuer werden dann automatisch gebucht.
            </p>
          </div>
        </SheetContent>
      </Sheet>

    </PageShell>
  );
}
